import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import { Ad, EditAd, EditAdCircuit, MediaFolder } from "../../types";
import PageHeader from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import { useParams } from "react-router-dom";
import Text from "../../components/UI/Text";
import { StyledAdCircuitContent } from "./style";
import { Container, Row } from "../../components/common/Grid";
import { Button } from "../../components/UI/Button";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import AddIcon from "@mui/icons-material/Add";
import { useSnackbar } from "notistack";
import { observer } from "mobx-react";
import Loading from "../../components/common/Loading";
import EmptySpace from "../../components/common/EmptySpace";
import AdvertisingCircuitModal from "../../components/common/AdvertisingCircuit/AdvertisingCircuitModal/AdvertisingCircuitModal";
import AdModal from "../../components/ScreenAdvertisingDetail/AdModal";
import moment from "moment";

interface RouteParams {
  id: string;
}

const AdvertisingDetail: React.FC = observer(() => {
  const { adCircuitStore, programStore } = useStores();
  const { id } = useParams<RouteParams>();
  const { t } = useTranslation();
  const [ad, setAd] = useState<Ad>();
  const [isModalEditAdCircuitOpen, setIsModalEditAdCircuitOpen] =
    useState(false);
  const [setIsModalAdOpen, setsetIsModalAdOpen] = useState(false);
  const [isLoadingAdCircuit, setIsLoadingAdCircuit] = useState(false);
  const [isLoadingAd, setIsLoadingAd] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const modalEditAdCircuitHandler = () => {
    setIsModalEditAdCircuitOpen((open) => !open);
  };

  const modalAdHandler = () => {
    setsetIsModalAdOpen((open) => !open);
  };

  useEffect(() => {
    setIsLoadingAdCircuit(true);
    setIsLoadingAd(true);

    adCircuitStore
      .getAdCircuitByID(id)
      .then(() => setIsLoadingAdCircuit(false));
    adCircuitStore.getAds(id).then(() => setIsLoadingAd(false));
  }, [id]);

  const handleAdSubmit = useCallback(
    async (data: EditAd) => {
      setIsLoadingAd(true);

      data.adCircuitId = id;

      data.adCircuitId = id;
      data.canBeSkipped === "canBeSkipped"
        ? (data.canBeSkipped = true)
        : (data.canBeSkipped = false);

      try {
        await adCircuitStore.addAd(data).then(() => setIsLoadingAd(false));
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      adCircuitStore.getAdCircuitByID(id);
      setAd(undefined);
      modalAdHandler();
    },
    [adCircuitStore, enqueueSnackbar, id, t]
  );

  const handleAdUpdate = useCallback(
    async (data: EditAd) => {
      setIsLoadingAd(true);
      data.adCircuitId = id;
      data.canBeSkipped === "canBeSkipped"
        ? (data.canBeSkipped = true)
        : (data.canBeSkipped = false);
      try {
        await adCircuitStore.updateAd(data).then(() => setIsLoadingAd(false));
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      adCircuitStore.getAdCircuitByID(id);
      setAd(undefined);
      modalAdHandler();
    },
    [adCircuitStore, enqueueSnackbar, id, t]
  );

  const handleAdCircuitSubmit = useCallback(
    async (data: EditAdCircuit) => {
      setIsLoadingAdCircuit(true);
      try {
        await adCircuitStore
          .updateAdCircuit(data)
          .then(() => setIsLoadingAdCircuit(false));
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      modalEditAdCircuitHandler();
    },
    [adCircuitStore, enqueueSnackbar, t]
  );

  const handleDeleteAd = useCallback(
    async (adID: string) => {
      setIsLoadingAd(true);
      try {
        await adCircuitStore.deleteAd(adID).then(() => setIsLoadingAd(false));
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }

      adCircuitStore.getAdCircuitByID(id);
    },
    [adCircuitStore, enqueueSnackbar, id, t]
  );

  const handleMediaFolderChange = useCallback(
    (folderId: MediaFolder["projectId"]) => {
      programStore.fetchMediaList(folderId);
    },
    [programStore]
  );

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.advertisingDetail.column.title"),
        dataKey: "title",
        render: (name: string) => name,
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.programDetail.column.url"),
        dataKey: "externalId",
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.advertisingDetail.column.validFrom"),
        dataKey: "validFrom",
        render: (validFrom: Date) => moment(validFrom).format("DD/MM/YYYY"),
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.advertisingDetail.column.validUntil"),
        dataKey: "validUntil",
        render: (validUntil: Date) => moment(validUntil).format("DD/MM/YYYY"),
      },
      {
        width: 90,
        flexGrow: 0,
        label: t("common.action"),
        dataKey: "id",
        render: (id: string, adItem: Ad) => {
          return (
            <>
              <IconButton
                aria-label="delete"
                onClick={() => {
                  setAd(adItem);
                  modalAdHandler();
                }}
              >
                <EditIcon />
              </IconButton>
              <div></div>

              <PopupState variant="popover" popupId="demo-popup-popover">
                {(popupState) => (
                  <div>
                    <IconButton color="error" {...bindTrigger(popupState)}>
                      <DeleteIcon />
                    </IconButton>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <Typography sx={{ p: 1 }}>
                        {t("common.deletePopUp")}
                        <IconButton
                          color="error"
                          sx={{ p: 0 }}
                          onClick={() => handleDeleteAd(id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Typography>
                    </Popover>
                  </div>
                )}
              </PopupState>
            </>
          );
        },
      },
    ],
    [handleDeleteAd, t]
  );

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px" fullHeight align="top">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.advertisingDetail.breadcrumb.home")}
          </BreadcrumbItem>
          <BreadcrumbItem path="/advertising-circuit">
            {t("screens.advertisingDetail.breadcrumb.adCircuit")}
          </BreadcrumbItem>
          <BreadcrumbItem active>
            {adCircuitStore.adCircuit?.title || ""}
          </BreadcrumbItem>
        </Breadcrumb>
        <PageHeader
          title={adCircuitStore.adCircuit?.title || ""}
          buttonEnable={true}
          buttonText={t("screens.advertisingDetail.pageHeader.buttonText")}
          handleButton={modalEditAdCircuitHandler}
          icon={<EditIcon />}
        />
        {isLoadingAdCircuit ? (
          <Loading />
        ) : (
          <StyledAdCircuitContent>
            {adCircuitStore.adCircuit?.description && (
              <Text paddingTop="20px">
                {adCircuitStore.adCircuit?.description}
              </Text>
            )}
          </StyledAdCircuitContent>
        )}
        <Container>
          <Row justifyContent={"end"}>
            <Button
              variant="contained"
              // startIcon={<AddIcon />}
              onClick={modalAdHandler}
            >
              <AddIcon /> {t("screens.advertisingDetail.newAd")}
            </Button>
          </Row>
        </Container>
        {isLoadingAd ? (
          <Loading />
        ) : isLoadingAd ? (
          <Loading />
        ) : !isLoadingAd && adCircuitStore.ads.length > 0 ? (
          <div
            style={{
              flex: "1 1 auto",
              width: "100%",
              height: `${(adCircuitStore.ads.length + 1) * 50}px`,
              display: "flex",
              marginTop: "20px",
            }}
          >
            <VirtualizedTable
              key={adCircuitStore.ads.length}
              rowCount={adCircuitStore.ads.length}
              rowGetter={({ index }) => adCircuitStore.ads[index]}
              columns={columns}
              rowStyle={(row) =>
                row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
              }
            />
          </div>
        ) : (
          <EmptySpace
            title={t("screens.advertisingDetail.emptyData")}
            buttonEnable={true}
            buttonText={t("screens.advertisingDetail.newAd")}
            handleButton={modalAdHandler}
          />
        )}
      </Content>

      <AdModal
        ad={ad}
        isSubmitting={false}
        isOpen={setIsModalAdOpen}
        onClose={() => {
          setAd(undefined);
          modalAdHandler();
        }}
        onSubmit={ad ? handleAdUpdate : handleAdSubmit}
        onChangeFolder={handleMediaFolderChange}
        medias={programStore.mediaList}
      />

      <AdvertisingCircuitModal
        adCircuit={adCircuitStore.adCircuit}
        isSubmitting={false}
        isOpen={isModalEditAdCircuitOpen}
        onClose={modalEditAdCircuitHandler}
        onSubmit={handleAdCircuitSubmit}
      />
    </Layout>
  );
});

export default AdvertisingDetail;
